"use client"

import React, { useState } from 'react'
import { cn } from '@/shared/utils/tw'
import type { KioskCoverModalProps } from '../types/kiosk-config'

/**
 * 키오스크 커버 모달 (Figma 절대 위치 기반)
 *
 * Figma 디자인: node-id=2059-26
 * 전체 레이아웃: 2160 x 3840 (세로 키오스크)
 *
 * Figma 좌표:
 * - 타이틀: x=200, y=200, Pretendard 700 200px
 * - 버튼: x=330, y=3105, 1500x340px
 *
 * 로딩 UI 통합:
 * - 비디오와 데이터 로딩을 병렬로 수행
 * - 로딩 중에도 비디오 배경이 보이도록 반투명 오버레이 사용
 * - 부드러운 opacity 전환 효과 적용
 */
export const KioskCoverModal = ({
  isOpen,
  isLoading,
  loadingMessage,
  videoUrl = '/webm/cover.webm',
  title = '민원 관련 궁금한 점,\nAI가 바로 답해 드려요!',
  buttonText = '시작하기',
  onStart,
}: KioskCoverModalProps) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [videoError, setVideoError] = useState(false)

  // Figma 비율 계산 (2160x3840 기준)
  const figmaWidth = 2160
  const figmaHeight = 3840

  if (!isOpen) return null

  // 통합 로딩 상태: 비디오 로딩 중 OR 데이터 로딩 중
  const showOverlayLoading = !isVideoLoaded || isLoading

  return (
    <div className="fixed inset-0 z-[100] bg-[#C9C6C7]">
      {/* 통합 로딩 오버레이 - 반투명으로 비디오가 뒤에서 보임 */}
      <div
        className={cn(
          "absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm",
          "transition-opacity duration-300",
          showOverlayLoading ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
          <p className="text-white/80 text-lg font-pretendard">
            {loadingMessage || '준비 중...'}
          </p>
        </div>
      </div>

      {/* 메인 콘텐츠 - opacity 전환 */}
      <div
        className={cn(
          "absolute inset-0 z-10",
          "transition-opacity duration-300",
          showOverlayLoading ? "opacity-0" : "opacity-100"
        )}
      >
        {/* === 전체 화면 레이어 (비디오 + 그라디언트) === */}

        {/* 비디오 로드 실패 시 fallback 그라디언트 배경 */}
        {videoError && (
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A4766] to-[#011828]" />
        )}

        {/* 동영상 배경 - 전체 화면 커버 */}
        <video
          autoPlay
          loop
          muted
          playsInline
          onCanPlay={() => setIsVideoLoaded(true)}
          onError={() => {
            setVideoError(true)
            setIsVideoLoaded(true)
          }}
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            "transition-opacity duration-500",
            isVideoLoaded && !videoError ? "opacity-100" : "opacity-0"
          )}
        >
          <source src={videoUrl} type="video/webm" />
        </video>

        {/* 흰색 그라디언트 오버레이 - 전체 화면 기준 */}
        <div
          className="absolute inset-x-0 top-0 z-[5] pointer-events-none"
          style={{
            height: `${(1810 / figmaHeight) * 100}%`,
            background: 'linear-gradient(180deg, rgba(255,255,255,1) 40%, rgba(255,255,255,0) 100%)',
          }}
        />

        {/* === 9:16 UI 컨테이너 (타이틀 + 버튼 비율 유지) === */}
        <div
          className="relative z-10 h-full mx-auto"
          style={{
            aspectRatio: '9/16',
            maxHeight: '100%',
            containerType: 'size',
          }}
        >
          {/* 라벨 + 타이틀 컨테이너 - Figma: node 3013:2947 */}
          <div
            className="absolute z-10 flex flex-col items-start"
            style={{
              left: `${(160 / figmaWidth) * 100}%`,
              top: `${(180 / figmaHeight) * 100}%`,
              gap: `${(64 / figmaHeight) * 100}cqh`,
            }}
          >
            {/* "시범 운영 중" 라벨 */}
            <div
              className="flex items-center justify-center font-pretendard font-semibold text-white"
              style={{
                fontSize: `${(130 / figmaWidth) * 100}cqw`,
                lineHeight: '1.4',
                background: 'rgba(62, 41, 57, 0.8)',
                borderRadius: `${(110 / figmaWidth) * 100}cqw`,
                paddingLeft: `${(60 / figmaWidth) * 100}cqw`,
                paddingRight: `${(60 / figmaWidth) * 100}cqw`,
                paddingTop: `${(20 / figmaHeight) * 100}cqh`,
                paddingBottom: `${(20 / figmaHeight) * 100}cqh`,
              }}
            >
              시범 운영 중
            </div>

            {/* 메인 타이틀 */}
            <h1
              className="font-pretendard font-bold text-[#3E2939] whitespace-pre-line"
              style={{
                fontSize: `${(180 / figmaWidth) * 100}cqw`,
                lineHeight: '1.36',
                textShadow: '0px 0px 50px rgba(0, 0, 0, 0.1)',
              }}
            >
              {title}
            </h1>
          </div>

          {/* CTA 버튼 - Figma: x=330, y=3105, 1500x340 */}
          <button
            type="button"
            onClick={onStart}
            disabled={showOverlayLoading}
            className={cn(
              'absolute z-10 flex items-center justify-center bg-white active:scale-[0.98] transition-all',
              showOverlayLoading && 'opacity-50 cursor-not-allowed'
            )}
            style={{
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: `${(360 / figmaHeight) * 100}%`,
              width: `${(1500 / figmaWidth) * 100}%`,
              height: `${(340 / figmaHeight) * 100}%`,
              borderRadius: '9999px',
              border: `${(12 / figmaWidth) * 100}cqw solid rgba(0, 0, 0, 0.06)`,
              boxShadow: '0px 0px 50px 0px rgba(0, 0, 0, 0.1)',
            }}
          >
            <span
              className="font-pretendard font-semibold"
              style={{
                color: '#0A1323',
                fontSize: `${(120 / figmaWidth) * 100}cqw`,
                lineHeight: '1.3',
              }}
            >
              {buttonText}
            </span>
          </button>

        </div>
      </div>
    </div>
  )
}

export default KioskCoverModal
